import svgPath from 'svgpath';
import { v4 as uuid } from 'uuid';
import type { TextRenderOutput, TextRenderResolution } from '@/framework/services/text-render';
import { formatSvgPathDecimals, generateGradient } from '@/utils/file-kit';
import { createSVGElement, parseSVG, removePaintAttributes, rewriteSVGIDsWithPrefix, SVG_NS } from '@/utils/svg-kit';
import {
    VectorType,
    type BoundingBox,
    type ILayerProps,
    type ILinearGradient,
    type IVectorLayerProps,
} from '@/version-types';
import { normalizeBorderRadius } from '../utils';
import { createBlurFilter, createShadowFilter } from '../utils/svg';
import { resolveVectorTextContentOffset } from '../utils/text-layout';
import { getVectorBaseRenderBox, getVectorBlurExpansion, getVectorRenderBox } from './render-box';

export type VectorBuildResult = {
    svg: SVGSVGElement;
    renderBox: BoundingBox;
};

export type VectorTextBuildResolution = Extract<
    TextRenderResolution<TextRenderOutput>,
    { readonly status: 'ready' } | { readonly status: 'empty' }
>;

class VectorLayerBuilder {
    private readonly props: IVectorLayerProps;

    private readonly root: SVGSVGElement;
    private readonly defs: SVGDefsElement;
    private readonly visual: SVGGElement;

    private readonly prefix: string; // id prefix
    private readonly baseID: string;
    private readonly transformedID: string;
    private readonly paintedID: string;

    private readonly baseRenderBox: BoundingBox;
    private readonly renderBox: BoundingBox;

    private polygonScale = '';
    private materialOpacity: string | null = null;

    /**
     * 创建一次性的 Vector 组合 builder。
     *
     * @param layer - 当前 Vector Layer。
     * @param source - Polygon / Graph 已就绪的 SVG 素材文本。
     * @param textResolution - 当前宿主 Renderer 发布的 ready/empty 文字结果。
     * @returns VectorLayerBuilder 实例。
     */
    constructor(
        private readonly layer: ILayerProps,
        private readonly source?: string,
        private readonly textResolution?: VectorTextBuildResolution,
    ) {
        this.props = layer.data as IVectorLayerProps;
        this.root = createSVGElement('svg', { xmlns: SVG_NS, preserveAspectRatio: 'none' });
        this.defs = createSVGElement('defs');
        this.visual = createSVGElement('g');

        // 每次构建使用独立 ID 前缀，避免同一图层重复构建或新旧 SVG 同时存在时发生引用串线。
        this.prefix = `vector-${layer.id}-${uuid()}`;
        this.baseID = `${this.prefix}-base`;
        this.transformedID = `${this.prefix}-transformed`;
        this.paintedID = `${this.prefix}-painted`;
        this.baseRenderBox = getVectorBaseRenderBox(layer);
        this.renderBox = getVectorRenderBox(layer, this.baseRenderBox, this.resolveTextRenderBox());
    }

    /**
     * 构建图形、图形特效、文字与共同 outer flip 组成的最终 SVG。
     *
     * @returns SVG 节点与组合 Render Box；图形素材无效时返回 null。
     */
    build(): VectorBuildResult | null {
        const shape = this.createShape();
        if (!shape) return null;

        // data-need-clippath 是素材图形元素上的内描边开关：值为字符串 "false" 时不添加裁切，
        // 并按配方的 strokeWidth 居中描边；缺省或其他值沿用 Polygon 的内描边策略，
        // 即先将描边加倍，再用原路径的填充区域裁切，只保留内侧的一半描边。
        // 开放直线子路径没有填充面积，若忽略 "false" 强制裁切，其描边会消失；
        // 因此裁切与描边倍率必须共用此判断，避免跳过裁切后仍把描边加粗一倍。
        const needsInnerStrokeClip =
            this.props.vectorType === VectorType.Polygon && shape.getAttribute('data-need-clippath') !== 'false';
        if (needsInnerStrokeClip) {
            this.applyPolygonInnerStrokeClip(shape);
        }

        shape.setAttribute('id', this.baseID);
        this.defs.appendChild(shape);

        const transformed = createSVGElement('g', {
            id: this.transformedID,
            transform: [this.extractTransform(), this.polygonScale].filter(Boolean).join(' '),
        });
        transformed.appendChild(createSVGElement('use', { href: `#${this.baseID}` }));
        this.defs.appendChild(transformed);

        const painted = createSVGElement('g', { id: this.paintedID });
        const paintedUse = createSVGElement('use', { href: `#${this.transformedID}` });
        if (this.props.vectorType !== VectorType.Graph) {
            this.applySimpleShapePaint(paintedUse, needsInnerStrokeClip);
        }
        painted.appendChild(paintedUse);
        this.defs.appendChild(painted);

        this.root.setAttribute('width', String(Math.ceil(this.renderBox.width)));
        this.root.setAttribute('height', String(Math.ceil(this.renderBox.height)));
        this.root.setAttribute(
            'viewBox',
            `${this.renderBox.left} ${this.renderBox.top} ${this.renderBox.width} ${this.renderBox.height}`,
        );
        this.root.style.overflow = 'visible';
        this.root.appendChild(this.defs);

        this.appendShadow();
        this.appendReflection();

        const mainUse = createSVGElement('use', { href: `#${this.paintedID}` });
        if (this.props.blur > 0) {
            const blurFilterID = `${this.prefix}-blur-filter`;
            const blurExpansion = getVectorBlurExpansion(this.props.blur);
            const blurFilterBox: BoundingBox = {
                left: this.baseRenderBox.left - blurExpansion,
                top: this.baseRenderBox.top - blurExpansion,
                width: this.baseRenderBox.width + blurExpansion * 2,
                height: this.baseRenderBox.height + blurExpansion * 2,
            };
            this.defs.appendChild(createBlurFilter(blurFilterID, blurFilterBox, this.props.blur));
            mainUse.setAttribute('filter', `url(#${blurFilterID})`);
        }

        this.visual.appendChild(mainUse);
        // 旧版素材根节点的透明度作用于图形及其特效的合成结果，不能只乘到 path 上，
        // 否则投影重叠区域会变深；也不能作用于外层 visual，否则会影响独立的 Vector Text。
        if (this.materialOpacity !== null) {
            const material = createSVGElement('g', { opacity: this.materialOpacity });
            material.append(...Array.from(this.visual.childNodes));
            this.visual.appendChild(material);
        }
        this.appendText();

        // 翻转必须作用于完整视觉结果，而不能只作用于主体。投影和倒影在 visual 内先完成合成，
        // 再与主体一起进入同一翻转坐标系，保持 Figma 的图层翻转语义。
        this.visual.setAttribute('transform', this.extractFlipTransform());
        this.root.appendChild(this.visual);

        return { svg: this.root, renderBox: this.renderBox };
    }

    /**
     * 计算文字自然外溢在 Vector 局部坐标系中的可见范围。
     *
     * @returns ready 文字的偏移 Render Box；empty 或未提供文字结果时返回 undefined。
     */
    private resolveTextRenderBox(): BoundingBox | undefined {
        if (this.textResolution?.status !== 'ready') return undefined;

        const { renderBox, snapshot } = this.textResolution.value;
        // 与编辑光标、选区共用同一条快照到图层坐标的映射，保证正文与编辑几何严格对齐；
        // renderBox 相对快照原点的自然字形外溢在平移后完整保留。
        const offset = resolveVectorTextContentOffset(this.layer, snapshot);

        return {
            left: offset.x + renderBox.left,
            top: offset.y + renderBox.top,
            width: renderBox.width,
            height: renderBox.height,
        };
    }

    /**
     * 把 ready 文字 SVG 作为图形特效之后的独立视觉节点加入组合。
     *
     * 文字节点拥有独立资源命名空间，避免连续 Preview 的 SVG defs 相互串线；节点保持在
     * 图形 filter 之外，并与图形一起接受 visual group 上唯一一次 outer flip。
     *
     * @returns void。
     */
    private appendText(): void {
        if (this.textResolution?.status !== 'ready') return;

        const output = this.textResolution.value;
        const renderBox = this.resolveTextRenderBox()!;
        const text = rewriteSVGIDsWithPrefix(output.svg.cloneNode(true) as SVGSVGElement, `${this.prefix}-text`);
        const opacity = Math.max(0, Math.min(1, this.props.textInfo?.opacity ?? 1));

        text.setAttribute('data-vector-text', 'true');
        text.setAttribute('x', String(renderBox.left));
        text.setAttribute('y', String(renderBox.top));
        text.setAttribute('width', String(renderBox.width));
        text.setAttribute('height', String(renderBox.height));
        text.setAttribute('opacity', String(opacity));
        text.style.overflow = 'visible';
        this.visual.appendChild(text);
    }

    /**
     * 提取翻转前的内部图片变换。
     *
     * 翻转由最外层统一处理，这里只负责缩放、旋转和偏移，避免投影与倒影留在翻转坐标系之外。
     *
     * @returns 可直接写入 SVG group 的 transform 属性。
     */
    private extractTransform(): string {
        const transform = this.props.imageTransform;
        if (!transform) return '';

        const { widthRatio = 1, heightRatio = 1, scale = 1, rotate = 0, offsetX = 0, offsetY = 0 } = transform;
        const scaledWidth = this.layer.width * widthRatio * scale;
        const scaledHeight = this.layer.height * heightRatio * scale;
        const centerX = this.layer.width / 2 + offsetX * scaledWidth;
        const centerY = this.layer.height / 2 + offsetY * scaledHeight;
        const scaleX = widthRatio * scale;
        const scaleY = heightRatio * scale;

        return [
            `translate(${centerX} ${centerY})`,
            `rotate(${rotate})`,
            `scale(${scaleX} ${scaleY})`,
            `translate(${-this.layer.width / 2} ${-this.layer.height / 2})`,
        ].join(' ');
    }

    /**
     * 提取围绕图层中心的最终翻转变换。
     *
     * 双轴翻转由两个镜像矩阵自然组合，不再为旋转角度维护额外的翻转分支。
     *
     * @returns 可作用于完整视觉合成结果的 SVG transform；未翻转时返回空字符串。
     */
    private extractFlipTransform(): string {
        const { flipH = false, flipV = false } = this.props.imageTransform || {};
        if (!flipH && !flipV) return '';

        return [
            `translate(${this.layer.width / 2} ${this.layer.height / 2})`,
            `scale(${flipH ? -1 : 1} ${flipV ? -1 : 1})`,
            `translate(${-this.layer.width / 2} ${-this.layer.height / 2})`,
        ].join(' ');
    }

    /**
     * 创建矩形路径，支持四个独立圆角并让描边保持在图层主体内。
     *
     * @returns 矩形路径节点。
     */
    private createRectShape(): SVGPathElement {
        const strokeWidth = this.props.strokeHidden ? 0 : this.props.strokeWidth || 0;
        const width = Math.max(0, this.layer.width - strokeWidth);
        const height = Math.max(0, this.layer.height - strokeWidth);
        const offset = strokeWidth / 2;
        const borderRadius = normalizeBorderRadius(this.props);
        const maxRadius = Math.ceil(Math.min(width, height) / 2);
        const tl = Math.min(borderRadius.leftTop, maxRadius);
        const tr = Math.min(borderRadius.rightTop, maxRadius);
        const br = Math.min(borderRadius.rightBottom, maxRadius);
        const bl = Math.min(borderRadius.leftBottom, maxRadius);
        const path = `
            M ${tl + offset},${offset}
            H ${width - tr + offset}
            A ${tr},${tr} 0 0 1 ${width + offset},${tr + offset}
            V ${height - br + offset}
            A ${br},${br} 0 0 1 ${width - br + offset},${height + offset}
            H ${bl + offset}
            A ${bl},${bl} 0 0 1 ${offset},${height - bl + offset}
            V ${tl + offset}
            A ${tl},${tl} 0 0 1 ${tl + offset},${offset}
            Z
        `;

        return createSVGElement('path', { d: formatSvgPathDecimals(svgPath(path).toString()) });
    }

    /**
     * 创建椭圆节点，并通过半个描边宽度的内缩避免主体画幅裁切描边。
     *
     * @returns 椭圆节点。
     */
    private createEllipseShape(): SVGEllipseElement {
        const strokeWidth = this.props.strokeHidden ? 0 : this.props.strokeWidth || 0;

        return createSVGElement('ellipse', {
            cx: this.layer.width / 2,
            cy: this.layer.height / 2,
            rx: Math.max(0, (this.layer.width - strokeWidth) / 2),
            ry: Math.max(0, (this.layer.height - strokeWidth) / 2),
        });
    }

    /**
     * 从 Polygon 素材中提取旧版支持的图形元素并适配图层尺寸
     *
     * path 沿用坐标缩放，保持配方描边宽度；其余原生元素沿用旧版整张素材缩放，
     * 使几何和描边一起缩放，不另写 points 或曲线转换器。
     *
     * @param source - 已就绪的 Polygon SVG 素材文本。
     * @returns 图形节点；素材无支持的图形或 path 无路径数据时返回 null
     */
    private createPolygonShape(source: string): SVGElement | null {
        const root = parseSVG(source);
        const originShape =
            root?.querySelector<SVGElement>('path') || root?.querySelector<SVGElement>('polygon, polyline, line');

        if (!root || !originShape) return null;

        const naturalWidth = this.props.naturalWidth || this.layer.width;
        const naturalHeight = this.props.naturalHeight || this.layer.height;
        const shape = document.importNode(originShape, true);
        removePaintAttributes(shape);
        this.materialOpacity = root.style.opacity || root.getAttribute('opacity');

        if (shape.localName === 'path') {
            const pathValue = shape.getAttribute('d');
            if (!pathValue) return null;

            shape.setAttribute(
                'd',
                formatSvgPathDecimals(
                    svgPath(pathValue)
                        .scale(this.layer.width / naturalWidth, this.layer.height / naturalHeight)
                        .toString(),
                ),
            );
            // 旧版 SVGCreator 对非矩形 path 固定使用 evenodd，并不消费配方 fillRule。
            // 在路径上显式保留该规则，避免继承 paintedUse 的 nonzero 后把旧素材的镂空填实。
            shape.setAttribute('fill-rule', 'evenodd');
        } else {
            this.polygonScale = `scale(${this.layer.width / naturalWidth} ${this.layer.height / naturalHeight})`;
        }

        return shape;
    }

    private createShape(): SVGElement | null {
        if (this.props.vectorType === VectorType.Rect) return this.createRectShape();
        if (this.props.vectorType === VectorType.Ellipse) return this.createEllipseShape();
        if (this.props.vectorType === VectorType.Polygon && this.source) {
            return this.createPolygonShape(this.source);
        }
        if (this.props.vectorType === VectorType.Graph && this.source) {
            return this.createGraphShape(this.source);
        }

        return null;
    }

    /**
     * 创建完成颜色替换、尺寸适配和 ID 隔离的 Graph 图形节点。
     *
     * @param source - 包含颜色占位符的 Graph SVG 文本。
     * @returns 可加入渲染树的图形节点；SVG 无法解析时返回 null。
     */
    private createGraphShape(source: string): SVGGElement | null {
        let coloredSource = source;
        const colors: string[] = [];

        this.props.colors?.forEach(({ color, values }) => {
            values.forEach((valueIndex) => {
                colors[valueIndex] = color;
            });
        });

        colors.forEach((color, index) => {
            coloredSource = coloredSource.replaceAll(`{{colors[${index}]}}`, color);
        });

        const root = parseSVG(coloredSource);
        if (!root) return null;

        const content = createSVGElement('g');
        const naturalWidth = this.props.naturalWidth || this.layer.width;
        const naturalHeight = this.props.naturalHeight || this.layer.height;
        const importedRoot = document.importNode(root, true) as SVGSVGElement;

        // Graph 素材的 viewBox 负责把素材坐标映射到 naturalWidth / naturalHeight。
        // 只复制根节点的 children 会丢掉该映射，导致 viewBox 与自然尺寸不一致的素材缩放错误。
        importedRoot.setAttribute('x', '0');
        importedRoot.setAttribute('y', '0');
        importedRoot.setAttribute('width', String(naturalWidth));
        importedRoot.setAttribute('height', String(naturalHeight));
        importedRoot.setAttribute('preserveAspectRatio', 'none');
        content.appendChild(importedRoot);

        rewriteSVGIDsWithPrefix(content, this.prefix);
        content.setAttribute(
            'transform',
            `scale(${this.layer.width / naturalWidth} ${this.layer.height / naturalHeight})`,
        );

        return content;
    }

    private appendGradient(id: string, gradient: ILinearGradient | null | ''): string | null {
        if (!gradient) return null;

        // generateGradient 返回 SVG 片段；包裹成根 SVG 后复用统一的解析和清理流程。
        const root = parseSVG(
            `<svg xmlns="${SVG_NS}">${generateGradient(id, this.layer.width, this.layer.height, gradient)}</svg>`,
        );
        const element = root?.firstElementChild;
        if (!element) return null;

        this.defs.appendChild(document.importNode(element, true));

        return `url(#${id})`;
    }

    private applySimpleShapePaint(use: SVGUseElement, needsInnerStrokeClip: boolean): void {
        const fill = this.appendGradient(`${this.prefix}-fill`, this.props.fillGradient) || this.props.fill;
        const stroke = this.appendGradient(`${this.prefix}-stroke`, this.props.strokeGradient) || this.props.stroke;

        use.setAttribute('fill', fill || 'none');
        use.setAttribute('fill-rule', this.props.fillRule || 'nonzero');

        if (this.props.strokeHidden || !stroke) return;

        const strokeRatio = needsInnerStrokeClip ? 2 : 1;
        use.setAttribute('stroke', stroke);
        use.setAttribute('stroke-width', String(this.props.strokeWidth * strokeRatio));
        use.setAttribute('stroke-linecap', this.props.strokeLinecap || 'butt');

        if (this.props.strokeDasharray) {
            const dasharray = this.props.strokeDasharray
                .split(',')
                .map((item) => parseFloat((parseFloat(item) * this.props.strokeWidth).toFixed(2)))
                .join(',');

            use.setAttribute('stroke-dasharray', dasharray);
        }
    }

    private applyPolygonInnerStrokeClip(shape: SVGElement): void {
        const clipID = `${this.prefix}-inner-stroke-clip`;
        const clipPath = createSVGElement('clipPath', { id: clipID });
        const clipShape = shape.cloneNode(true) as SVGElement;

        clipShape.removeAttribute('id');
        clipPath.appendChild(clipShape);
        this.defs.appendChild(clipPath);
        shape.setAttribute('clip-path', `url(#${clipID})`);
    }

    private appendShadow(): void {
        const shadow = this.props.effect?.shadow?.[0];
        if (!shadow || shadow.hidden) return;

        const shadowFilterID = `${this.prefix}-shadow-filter`;
        const shadowBlur = Math.max(0, shadow.blur);
        const shadowExpansion = getVectorBlurExpansion(shadowBlur);
        const baseRight = this.baseRenderBox.left + this.baseRenderBox.width;
        const baseBottom = this.baseRenderBox.top + this.baseRenderBox.height;
        const shadowLeft = this.baseRenderBox.left + shadow.x - shadowExpansion;
        const shadowTop = this.baseRenderBox.top + shadow.y - shadowExpansion;
        const shadowRight = baseRight + shadow.x + shadowExpansion;
        const shadowBottom = baseBottom + shadow.y + shadowExpansion;
        const filterLeft = Math.min(this.baseRenderBox.left, shadowLeft);
        const filterTop = Math.min(this.baseRenderBox.top, shadowTop);
        const shadowBox: BoundingBox = {
            left: filterLeft,
            top: filterTop,
            width: Math.max(baseRight, shadowRight) - filterLeft,
            height: Math.max(baseBottom, shadowBottom) - filterTop,
        };
        const shadowUse = createSVGElement('use', {
            href: `#${this.paintedID}`,
            filter: `url(#${shadowFilterID})`,
        });

        this.defs.appendChild(
            createShadowFilter(shadowFilterID, {
                box: shadowBox,
                x: shadow.x,
                y: shadow.y,
                blur: shadowBlur,
                color: shadow.color,
                opacity: shadow.opacity,
            }),
        );
        this.visual.appendChild(shadowUse);
    }

    private appendReflectionMask(box: BoundingBox): string {
        const gradientID = `${this.prefix}-reflection-gradient`;
        const maskID = `${this.prefix}-reflection-mask`;
        const gradient = createSVGElement('linearGradient', {
            id: gradientID,
            x1: '0%',
            y1: '0%',
            x2: '0%',
            y2: '100%',
        });
        const opaque = createSVGElement('stop', { offset: '0%', 'stop-color': 'white', 'stop-opacity': 1 });
        const transparent = createSVGElement('stop', { offset: '100%', 'stop-color': 'white', 'stop-opacity': 0 });
        const mask = createSVGElement('mask', {
            id: maskID,
            x: box.left,
            y: box.top,
            width: box.width,
            height: box.height,
            maskUnits: 'userSpaceOnUse',
        });
        const rect = createSVGElement('rect', {
            x: box.left,
            y: box.top,
            width: box.width,
            height: box.height,
            fill: `url(#${gradientID})`,
        });

        gradient.appendChild(opaque);
        gradient.appendChild(transparent);
        mask.appendChild(rect);
        this.defs.appendChild(gradient);
        this.defs.appendChild(mask);

        return `url(#${maskID})`;
    }

    private appendReflection(): void {
        const reflection = this.props.effect?.reflection?.[0];
        if (!reflection || reflection.hidden || reflection.size <= 0) return;

        const reflectionHeight = (this.baseRenderBox.height * Math.max(0, Math.min(100, reflection.size))) / 100;
        const blurExpansion = getVectorBlurExpansion(reflection.blur);
        const reflectionTranslateY = this.baseRenderBox.top * 2 + this.baseRenderBox.height * 2 + reflection.y;
        const reflectionBox: BoundingBox = {
            left: this.baseRenderBox.left - blurExpansion,
            top: this.baseRenderBox.top + this.baseRenderBox.height + reflection.y - blurExpansion,
            width: this.baseRenderBox.width + blurExpansion * 2,
            height: reflectionHeight + blurExpansion * 2,
        };
        const reflectionRoot = createSVGElement('g', {
            mask: this.appendReflectionMask(reflectionBox),
        });
        const reflectionGroup = createSVGElement('g', {
            transform: `translate(0 ${reflectionTranslateY}) scale(1 -1)`,
            opacity: reflection.opacity,
        });
        const reflectionUse = createSVGElement('use', { href: `#${this.paintedID}` });

        if (reflection.blur > 0) {
            const reflectionFilterID = `${this.prefix}-reflection-filter`;
            const reflectionFilterBox: BoundingBox = {
                left: this.baseRenderBox.left - blurExpansion,
                top: this.baseRenderBox.top - blurExpansion,
                width: this.baseRenderBox.width + blurExpansion * 2,
                height: this.baseRenderBox.height + blurExpansion * 2,
            };
            this.defs.appendChild(createBlurFilter(reflectionFilterID, reflectionFilterBox, reflection.blur));
            reflectionUse.setAttribute('filter', `url(#${reflectionFilterID})`);
        }

        reflectionGroup.appendChild(reflectionUse);
        reflectionRoot.appendChild(reflectionGroup);
        this.visual.appendChild(reflectionRoot);
    }
}

/**
 * 构建当前矢量图层的最终 SVG DOM。
 *
 * @param layer - 当前矢量图层。
 * @param source - Polygon / Graph 已就绪的素材文本；Rect / Ellipse 可省略。
 * @param textResolution - 当前真实宿主 Renderer 的 ready/empty 文字结果。
 * @returns SVG 节点与统一 Render Box；素材无效时返回 null。
 */
export const buildVectorLayer = (
    layer: ILayerProps,
    source?: string,
    textResolution?: VectorTextBuildResolution,
): VectorBuildResult | null => {
    return new VectorLayerBuilder(layer, source, textResolution).build();
};
